import React from "react";
import { ConsumerMedicaidLeadItemActionsProps } from "./types";
import { MedicaidLeadStatus } from "modules/medicaid/types";
import TabbedButton, {
  TabbedButtonMood,
  TabbedButtonType,
} from "components/TabbedButton";
import { useDispatch } from "react-redux";
import { go } from "modules/navigation/helpers/navigator";
import {
  getPartnerRoute,
  getRoute,
  ROUTE_TEMPLATE,
} from "modules/navigation/routes";
import { formatPartnerForRoute } from "modules/navigation/helpers/getFormattedPartnerId";
import {
  updateMedicaidLeadStatus,
  fetchMedicaidLeads,
  clearAdminRenderedDocuments,
} from "modules/medicaid/actions";
import { ModalType, showModal } from "components/Modal";
import RenderMedicaidDocumentsModal from "screens/medicaidAnalysis/admin/RenderMedicaidDocumentsModal";
import { Stack } from "@mui/material";
import { resubmitModal } from "./resubmitModal";

const ConsumerMedicaidLeadItemActions = ({
  item,
}: ConsumerMedicaidLeadItemActionsProps) => {
  const dispatch = useDispatch();
  const partnerIdForRoute =
    item.medicaidLead.partnerId != null
      ? formatPartnerForRoute(item.medicaidLead.partnerId)
      : null;
  const medicaidLeadId = item.medicaidLead.medicaidLeadId;

  const handleNavigateToInterview = () =>
    go(
      dispatch,
      partnerIdForRoute != null
        ? getPartnerRoute(
            partnerIdForRoute,
            ROUTE_TEMPLATE.PARTNER_MEDICAID_LEAD,
            { medicaidLeadId },
          )
        : getRoute(ROUTE_TEMPLATE.MEDICAID_LEAD, { medicaidLeadId }),
    );

  const handleNavigateToAssetProtectionAnalysis = () =>
    go(
      dispatch,
      partnerIdForRoute != null
        ? getPartnerRoute(
            partnerIdForRoute,
            ROUTE_TEMPLATE.PARTNER_MEDICAID_ANALYSIS,
            { medicaidLeadId },
          )
        : getRoute(ROUTE_TEMPLATE.MEDICAID_ANALYSIS, { medicaidLeadId }),
    );

  const handleRenderMedicaidDocuments = () => {
    dispatch(clearAdminRenderedDocuments());
    showModal({
      type: ModalType.CONFIRM,
      title: "Medicaid Documents",
      renderContent: () => (
        <RenderMedicaidDocumentsModal medicaidLeadId={medicaidLeadId} />
      ),
    });
  };

  const onSubmitHandle = async (reason: string) => {
    updateMedicaidLeadStatus(
      dispatch,
      medicaidLeadId,
      MedicaidLeadStatus.ReadyForPartner,
      reason,
    ).then(() => fetchMedicaidLeads(dispatch));
  };

  const handleOpenRejectModal = () => {
    resubmitModal({ onSubmit: onSubmitHandle });
  };

  switch (item.medicaidLead.status) {
    case MedicaidLeadStatus.InterviewInProgress:
      return (
        <TabbedButton
          label="Continue"
          onClick={handleNavigateToInterview}
          mood={TabbedButtonMood.POSITIVE}
          style={TabbedButtonType.PRIMARY}
        />
      );
    case MedicaidLeadStatus.ReadyForPartner:
      return (
        <TabbedButton
          label="Asset Protection Analysis"
          onClick={handleNavigateToAssetProtectionAnalysis}
          mood={TabbedButtonMood.POSITIVE}
        />
      );
    case MedicaidLeadStatus.LeadAccepted:
      return (
        <Stack direction="row" sx={{ gap: "10px", justifyContent: "center" }}>
          <TabbedButton
            label="Download Documents"
            onClick={handleRenderMedicaidDocuments}
            mood={TabbedButtonMood.POSITIVE}
            style={TabbedButtonType.PRIMARY}
          />
          <TabbedButton
            label="View Interview"
            onClick={handleNavigateToInterview}
            style={TabbedButtonType.NEUTRAL}
          />
        </Stack>
      );
    case MedicaidLeadStatus.LeadRejected:
      return (
        <Stack direction="row" sx={{ gap: "10px", justifyContent: "center" }}>
          <TabbedButton
            label="Resubmit for review"
            onClick={handleOpenRejectModal}
            mood={TabbedButtonMood.POSITIVE}
            style={TabbedButtonType.PRIMARY}
          />
          <TabbedButton
            label="View Interview"
            onClick={handleNavigateToInterview}
            style={TabbedButtonType.NEUTRAL}
          />
        </Stack>
      );
    case MedicaidLeadStatus.AwaitingAttorneyRequest:
      return (
        <TabbedButton
          label="Send Request to Attorney"
          onClick={handleNavigateToAssetProtectionAnalysis}
          mood={TabbedButtonMood.POSITIVE}
          style={TabbedButtonType.PRIMARY}
        />
      );
  }
};

export default ConsumerMedicaidLeadItemActions;
